/**
 * UMD brand colors as space-separated RGB triplets.
 *
 * Mirrors the hex values that previously lived in tailwind.config.js's
 * `umd-red` and `umd-gold` palettes. RGB-triplet form is required so the
 * Tailwind `rgb(var(--color-primary) / <alpha-value>)` pattern accepts
 * opacity modifiers like `bg-primary/50`.
 */
import type { ThemeColors } from '../types';

export const umdColors: ThemeColors = {
  // Primary — UMD Red (#E03A3E DEFAULT + 50…900 scale matching the
  // original umd-red palette).
  '--color-primary':     '224 58 62',
  // -strong: fills carrying white text — UMD red is 4.34:1 with white, so
  // solid buttons/bubbles use the 700 tier (6.47:1).
  '--color-primary-strong': '185 28 28',
  '--color-primary-50':  '254 242 242',
  '--color-primary-100': '254 226 226',
  '--color-primary-200': '254 202 202',
  '--color-primary-300': '252 165 165',
  '--color-primary-400': '248 113 113',
  '--color-primary-500': '239 68 68',
  '--color-primary-600': '220 38 38',
  '--color-primary-700': '185 28 28',
  '--color-primary-800': '153 27 27',
  '--color-primary-900': '127 29 29',

  // Accent — UMD Gold (#FFD520 official; 500 in the original scale).
  '--color-accent':     '255 213 32',
  '--color-accent-50':  '255 251 234',
  '--color-accent-100': '255 245 197',
  '--color-accent-200': '255 236 139',
  '--color-accent-300': '255 220 78',
  '--color-accent-400': '255 203 31',
  '--color-accent-500': '255 213 32',
  '--color-accent-600': '233 160 0',
  '--color-accent-700': '194 120 3',
  '--color-accent-800': '154 90 10',
  '--color-accent-900': '124 74 12',

  // Brand-mark faces (components/ui/BrandMark.tsx). Three tones of one hue,
  // stepping ~14% lightness apart so the oblique faces separate cleanly.
  // UMD Red sits at L≈55%, which leaves comfortable room to go darker twice —
  // so the brand color itself is the front face, the one that carries the
  // mark's identity. `-dm` is the dark-mode ramp, lifted so the darkest face
  // stays visible on the warm-charcoal surface.
  '--color-mark-front': '224 58 62',
  '--color-mark-top':   '176 36 42',
  '--color-mark-side':  '114 22 28',
  '--color-mark-front-dm': '244 118 120',
  '--color-mark-top-dm':   '214 74 78',
  '--color-mark-side-dm':  '158 44 50',
};
